import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.array.index-of.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.object.assign.js";
import "core-js/modules/es.function.bind.js";
import _pt from "prop-types";
var _excluded = ["id", "type", "href", "size", "dynamicWidth", "isLargeOnMobile", "variant", "disabled", "onClick", "className", "custom", "children", "loading", "loadingSpinnerProps"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import classnames from 'classnames';
import { ButtonOrLink } from '@lmig/lmds-react-button-or-link';
import "./button.css";
import { LoadingSpinner } from '@lmig/lmds-react-loading-spinner';
/**
 * @see [LMDS Button documentation and examples](https://designsystem.lmig.com/components/buttons/code)
 *
 * API:
 *
 * @implements [ButtonProps](https://designsystem.lmig.com/components/buttons/code#button-props)
 * @extends [HTMLAnchorElement](https://microsoft.github.io/PowerBI-JavaScript/interfaces/_node_modules_typedoc_node_modules_typescript_lib_lib_dom_d_.htmlanchorelement.html)
 * @extends [HTMLButtonElement](https://microsoft.github.io/PowerBI-JavaScript/interfaces/_node_modules_typedoc_node_modules_typescript_lib_lib_dom_d_.htmlbuttonelement.html)
 */
export var Button = function Button(_ref) {
  var id = _ref.id,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'button' : _ref$type,
    href = _ref.href,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    _ref$dynamicWidth = _ref.dynamicWidth,
    dynamicWidth = _ref$dynamicWidth === void 0 ? false : _ref$dynamicWidth,
    _ref$isLargeOnMobile = _ref.isLargeOnMobile,
    isLargeOnMobile = _ref$isLargeOnMobile === void 0 ? false : _ref$isLargeOnMobile,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'secondary' : _ref$variant,
    disabled = _ref.disabled,
    onClick = _ref.onClick,
    className = _ref.className,
    custom = _ref.custom,
    children = _ref.children,
    loading = _ref.loading,
    loadingSpinnerProps = _ref.loadingSpinnerProps,
    rest = _objectWithoutProperties(_ref, _excluded);
  var handleClick = function handleClick(event) {
    if (disabled || loading) {
      return event.preventDefault();
    }
    return onClick && onClick(event);
  };
  var classNames = classnames('lmig-Button', "lmig-Button--".concat(size), "lmig-Button--".concat(variant), {
    'lmig-Button--dynamicWidth': dynamicWidth
  }, {
    'lmig-Button--isLargeOnMobile': isLargeOnMobile
  }, {
    'lmig-Button--loading': loading
  }, className);
  return /*#__PURE__*/React.createElement(ButtonOrLink, _extends({
    id: id,
    type: type,
    className: classNames,
    href: href,
    custom: custom,
    "aria-disabled": disabled || loading,
    onClick: handleClick
  }, rest), loading && /*#__PURE__*/React.createElement(LoadingSpinner, _extends({
    hiddenTrack: true,
    color: "blue",
    size: "16"
  }, loadingSpinnerProps)), /*#__PURE__*/React.createElement("span", {
    className: "lmig-Button-text"
  }, children));
};
Button.propTypes = {
  /** Add an optional ID to this element: `<button class="lmig-Button"`. */id: _pt.string,
  /** Add custom classes to this element: `<button class="lmig-Button"`. */className: _pt.string,
  /**
     * Change the vertical height.
     * @default 'medium'
     */
  size: _pt.oneOf(['small', 'medium', 'large']),
  /**
     * Only one primary button should appear per page. Use inverse when placing a
     * button on a colored background.
     * @default 'secondary'
     */
  variant: _pt.oneOf(['primary', 'secondary', 'inverse']),
  /**
     * Expand a button to fit its container's width.
     * Should only be used within a grid column.
     * @default false
     */
  dynamicWidth: _pt.bool,
  /**
     * If 'true', small and medium buttons will render as large buttons in mobile portrait layouts (0-479px).
     * @default false
     */
  isLargeOnMobile: _pt.bool,
  /**
     * Set the button type, if necessary. Use 'submit' for a form submit button,
     * and 'reset' within forms to reset inputs.
     * @default 'button'
     */
  type: _pt.oneOf(['button', 'submit', 'reset']),
  /** When an href is provided, an anchor tag is generated. Additional anchor
     * attributes may be passed in as **Button** props (e.g., target). */
  href: _pt.string,
  /**
     * Prevent interaction on the element.
     */
  disabled: _pt.bool,
  /** Pass additional functionality to run on click events. */onClick: _pt.func,
  /** Set button text inside this element: `<span class="lmig-Button-text">` */children: _pt.node.isRequired,
  /**
     * Sets the button loading status and shows the loading spinner
     */
  loading: _pt.bool,
  /**
     * Sets custom props for the loading spinner. (The loading spinner's `hiddenTrack`, `size`,
     * and `color` props are set within `Button` and can't be set using `loadingSpinnerProps`.)
     */
  loadingSpinnerProps: _pt.object
};